// src/components/BuyCritsModal.js
// ============================================================================
// IN-PLACE CRIT-PACK PURCHASE MODAL (founder 2026-07-13)
//
// Shown when an action fails on insufficient Crits (event creation: entry +
// size unlock + title fee exceeded balance). Sibling of UpgradeMembershipModal
// and the same conversion philosophy: the entire funnel — shortfall, pack
// pick, embedded Stripe checkout, success — happens INSIDE the modal, with
// the user's filled-out form alive behind the scrim. No navigation away.
//
// Packs come from getCritPurchaseOptions (live catalog; server prices always
// win) with a static fallback; checkout via createCritPurchaseCheckout, the
// exact SubscriptionScreen "Buy Crits" flow. Packs that cover the shortfall
// get a "COVERS IT" tag so the impulse choice is obvious.
// ============================================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View, Text, Modal, Pressable, ScrollView, ActivityIndicator, StyleSheet, Platform,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { THEME } from '../constants/uiTheme';
import useCritsEnabled from '../hooks/useCritsEnabled';

const IS_WEB = Platform.OS === 'web';
const C = THEME.colors; // brand #FCD000, surface #111111, borders #222/#333

// Fallback mirror of payments/stripeDeposits.js CRIT_PACKS — used only if the
// live catalog call fails; server-side prices always govern the charge.
const FALLBACK_PACKS = [
  { id: 'spark', name: 'Spark', crits: 10, priceCents: 499 },
  { id: 'surge', name: 'Surge', crits: 25, priceCents: 1199 },
  { id: 'overdrive', name: 'Overdrive', crits: 50, priceCents: 2199 },
  { id: 'maxed', name: 'Maxed', crits: 125, priceCents: 4999 },
];

const fmtPrice = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

export default function BuyCritsModal({
  visible,
  onClose,
  onPurchased,     // called when the user taps "Back to my event" after success
  onUpgradePlan,   // optional: link to the Subscription screen (more monthly Crits)
  shortfall,       // optional { have, need } parsed from the rejection
}) {
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled — whole modal hides when off
  const [step, setStep] = useState('packs'); // 'packs' | 'checkout' | 'success'
  const [packs, setPacks] = useState(FALLBACK_PACKS);
  const [busyPack, setBusyPack] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [embedded, setEmbedded] = useState(null); // { clientSecret, publishableKey }
  const checkoutContainerRef = useRef(null);
  const checkoutInstanceRef = useRef(null);

  const missing = shortfall && shortfall.need > shortfall.have
    ? shortfall.need - shortfall.have
    : 0;

  // Live pack catalog (prices/names can change server-side).
  useEffect(() => {
    if (!visible || !critsEnabled) return;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'getCritPurchaseOptions');
        const res = await fn();
        if (Array.isArray(res.data?.packs) && res.data.packs.length > 0) {
          setPacks(res.data.packs);
        }
      } catch (e) { /* fallback list stands */ }
    })();
  }, [visible, critsEnabled]);

  const teardownCheckout = useCallback(() => {
    try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
    checkoutInstanceRef.current = null;
  }, []);

  const reset = useCallback(() => {
    teardownCheckout();
    setEmbedded(null);
    setStep('packs');
    setBusyPack(null);
    setErrorMsg(null);
  }, [teardownCheckout]);

  const close = useCallback(() => {
    reset();
    if (onClose) onClose();
  }, [reset, onClose]);

  // Crit economy off: there is nothing to buy, so hand control straight back to
  // the caller via close() rather than sitting invisible. Otherwise the caller's
  // shortfall state would stick open with no modal and no way to clear it.
  useEffect(() => {
    if (visible && !critsEnabled) close();
  }, [visible, critsEnabled, close]);

  const buyPack = async (packId) => {
    setBusyPack(packId);
    setErrorMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'createCritPurchaseCheckout');
      const res = await fn({ packId });
      const d = res.data || {};
      if (IS_WEB && d.clientSecret) {
        setEmbedded({ clientSecret: d.clientSecret, publishableKey: d.publishableKey });
        setStep('checkout');
      } else if (d.url && typeof window !== 'undefined') {
        window.location.href = d.url; // native/hosted fallback
      } else {
        throw new Error('Could not start purchase.');
      }
    } catch (err) {
      console.error('[BuyCritsModal] buy pack failed:', err);
      setErrorMsg(String(err?.message || 'Could not start purchase. Please try again.'));
    } finally {
      setBusyPack(null);
    }
  };

  // Mount the embedded checkout into the raw DOM node (RN Web renders real
  // divs; Stripe injects its iframe there). Same lifecycle as SubscriptionScreen.
  useEffect(() => {
    if (!IS_WEB || !embedded?.clientSecret || step !== 'checkout') return;
    let cancelled = false;
    (async () => {
      try {
        if (!embedded.publishableKey) throw new Error('Stripe publishable key is not configured.');
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(embedded.publishableKey);
        if (cancelled || !stripe) return;
        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret: embedded.clientSecret,
          onComplete: () => {
            // Payment captured — the webhook credits the Crits async; by the
            // time they dismiss and re-hit Create it's essentially landed.
            teardownCheckout();
            setEmbedded(null);
            setStep('success');
          },
        });
        if (cancelled) { try { instance.destroy(); } catch (e) { /* noop */ } return; }
        checkoutInstanceRef.current = instance;
        if (checkoutContainerRef.current) instance.mount(checkoutContainerRef.current);
      } catch (err) {
        console.error('[BuyCritsModal] embedded checkout mount failed:', err);
        if (!cancelled) {
          setEmbedded(null);
          setStep('packs');
          setErrorMsg('Could not open checkout. Please try again.');
        }
      }
    })();
    return () => {
      cancelled = true;
      teardownCheckout();
    };
  }, [embedded, step, teardownCheckout]);

  if (!visible || !critsEnabled) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <View style={styles.overlay}>
        <Pressable style={styles.scrim} onPress={step === 'checkout' ? undefined : close} />
        <View style={styles.card}>

          {step === 'packs' && (
            <>
              <Text style={styles.title}>Top Up Your Crits</Text>
              <Text style={styles.subtitle}>
                {missing > 0
                  ? `You're ${missing} Crits short for this event. Grab a pack and launch it — your setup is saved right behind this window.`
                  : 'Grab a Crit pack and get back to your event — your setup is saved right behind this window.'}
              </Text>
              {errorMsg && <Text style={styles.error}>{errorMsg}</Text>}

              {packs.map((p) => {
                const covers = missing > 0 && Number(p.crits) >= missing;
                return (
                  <Pressable
                    key={p.id}
                    style={[styles.packRow, covers && styles.packRowCovers]}
                    onPress={() => !busyPack && buyPack(p.id)}
                    disabled={!!busyPack}
                  >
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={styles.packName}>{p.name}</Text>
                        {covers && <Text style={styles.coversTag}>COVERS IT</Text>}
                      </View>
                      <Text style={styles.packCrits}>{p.crits} Crits</Text>
                    </View>
                    {busyPack === p.id
                      ? <ActivityIndicator color={C.brand} />
                      : <Text style={styles.packPrice}>{fmtPrice(p.priceCents)}</Text>}
                  </Pressable>
                );
              })}

              <View style={styles.footerRow}>
                <Pressable onPress={close} hitSlop={8}>
                  <Text style={styles.footerLink}>Not now</Text>
                </Pressable>
                {onUpgradePlan && (
                  <Pressable onPress={() => { close(); onUpgradePlan(); }} hitSlop={8}>
                    <Text style={styles.footerLink}>More Crits monthly — upgrade plan →</Text>
                  </Pressable>
                )}
              </View>
            </>
          )}

          {step === 'checkout' && (
            <>
              <View style={styles.checkoutHeader}>
                <Text style={styles.title}>Buy Crits</Text>
                <Pressable onPress={() => { teardownCheckout(); setEmbedded(null); setStep('packs'); }} hitSlop={8}>
                  <Text style={styles.closeX}>✕</Text>
                </Pressable>
              </View>
              <ScrollView style={styles.checkoutScroll} showsVerticalScrollIndicator={false}>
                {/* Stripe mounts its embedded checkout iframe into this div. */}
                <div ref={checkoutContainerRef} style={{ width: '100%', minHeight: 380 }} />
              </ScrollView>
            </>
          )}

          {step === 'success' && (
            <>
              <Text style={styles.successEmoji}>⚡</Text>
              <Text style={styles.title}>Crits incoming!</Text>
              <Text style={styles.subtitle}>
                Your pack is on the way to your balance. Hit Create Event again
                and launch the event you just built.
              </Text>
              <Pressable
                style={styles.primaryBtn}
                onPress={() => { const done = onPurchased || close; reset(); done(); }}
              >
                <Text style={styles.primaryBtnText}>Back to my event</Text>
              </Pressable>
            </>
          )}

        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  scrim: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.8)' },
  card: {
    width: '100%', maxWidth: 460, backgroundColor: C.surface, borderRadius: THEME.radius.lg,
    borderWidth: 1, borderColor: C.borderStrong, padding: THEME.space.xl,
  },
  title: { color: C.brand, fontSize: 18, fontWeight: THEME.font.weightBold, textAlign: 'center' },
  subtitle: { color: C.textMuted, fontSize: 13, textAlign: 'center', marginTop: 8, lineHeight: 19 },
  error: { color: '#ff6b6b', fontSize: 12, textAlign: 'center', marginTop: 8 },
  packRow: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: C.chip,
    borderWidth: 1, borderColor: C.border, borderRadius: THEME.radius.md,
    paddingVertical: 14, paddingHorizontal: 16, marginTop: 12,
  },
  packRowCovers: { borderColor: C.brand },
  packName: { color: C.text, fontSize: 15, fontWeight: THEME.font.weightBold },
  coversTag: {
    color: C.bg, backgroundColor: C.brand, fontSize: 9, fontWeight: THEME.font.weightHeavy,
    borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, marginLeft: 8, overflow: 'hidden',
  },
  packCrits: { color: C.textMuted, fontSize: 12, marginTop: 2 },
  packPrice: { color: C.brand, fontSize: 15, fontWeight: THEME.font.weightBold, marginLeft: 12 },
  footerRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 16 },
  footerLink: { color: C.textMuted, fontSize: 13, textDecorationLine: 'underline' },
  checkoutHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  closeX: { color: C.textMuted, fontSize: 18, paddingHorizontal: 4 },
  checkoutScroll: { maxHeight: 520, marginTop: 12 },
  successEmoji: { fontSize: 40, textAlign: 'center', marginBottom: 8 },
  primaryBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingVertical: 13,
    alignItems: 'center', marginTop: 18,
  },
  primaryBtnText: { color: C.bg, fontSize: 15, fontWeight: THEME.font.weightHeavy },
});
